import { useSettingsHostPathname } from "../../v2/src/hooks/useSettingsHostPathname";
import {
  DataGridProProps,
  GridApiPro,
  GridColDef,
  GridDensity,
  GridEvents,
  GridGroupNode,
  GridInitialState,
  GridPinnedColumnFields,
  GridRowId,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef,
  GridColumnsInitialState,
  GridPaginationModel,
} from "@mui/x-data-grid-pro";
import { RefObject, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useRegisterDataGridSaveEvents } from "./useRegisterDataGridSaveEvents";
import { isDefaultGridState } from "./gridStateUtils";
import { ShiGridColDefs } from "shared-ui/src/interfaces/ShiDataGridInterface";
import {
  useDeleteUserConfig,
  useGetUserConfig,
} from "../../modules/hooks/useUserConfig";
import { UserConfigDto } from "@lib/ShiOneClient";

interface UseCXUserPrefsParams {
  gridId: string | undefined;
  apiRef: RefObject<GridApiPro | null> | undefined;
  gridColsDef: ShiGridColDefs;
  rowExpandLookup: RefObject<Record<GridRowId, boolean>> | undefined;
  treeData: boolean;
  saveUserConfig: boolean;
}

interface CXGridPrefsState {
  columns: GridColumnsInitialState;
  pagination?: { paginationModel: GridPaginationModel };
  pinnedColumns?: GridPinnedColumnFields;
  sorting?: { sortModel?: GridSortModel };
  density?: GridDensity;
}

const GRID_EVENTS: GridEvents[] = [
  "pinnedColumnsChange",
  "columnOrderChange",
  "columnVisibilityModelChange",
  "sortModelChange",
  "columnResizeStop",
  "paginationModelChange",
];

export const DEFAULT_GRID_PAGINATION_MODEL: GridPaginationModel = {
  page: 0,
  pageSize: 25,
};

export function getMuiGridSettingsKey(pathname: string, gridId: string | undefined) {
  return `${pathname}_MuiGridSettings${gridId ? `:${gridId}` : ""}`;
}

function getSavedPageSize(settingsValue: string | undefined): number | undefined {
  if (!settingsValue) return undefined;
  try {
    const pageSize = JSON.parse(settingsValue)?.gridState?.pagination?.paginationModel?.pageSize;
    return typeof pageSize === "number" && pageSize > 0 ? pageSize : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolves the page size a `saveUserConfig` grid restores on mount, so server-paginated pages can
 * request that page size up front instead of refetching once the grid applies it.
 */
export function useCXSavedPaginationModel(gridId: string | undefined) {
  const hostPathname = useSettingsHostPathname();
  const { userConfig, isLoading } = useGetUserConfig(
    getMuiGridSettingsKey(hostPathname, gridId)
  );
  const pageSize =
    getSavedPageSize(userConfig?.settingsValue) ?? DEFAULT_GRID_PAGINATION_MODEL.pageSize;
  const paginationModel = useMemo<GridPaginationModel>(() => ({ page: 0, pageSize }), [pageSize]);

  return { paginationModel, isLoading };
}

function buildMergedState(defaultState: CXGridPrefsState, settingsValue: string): CXGridPrefsState {
  const parsedConfig = JSON.parse(settingsValue);
  const savedGridState = parsedConfig.gridState;

  // Build the columns state carefully to avoid column duplication
  // Restore columnVisibilityModel and orderedFields but preserve column definitions from props
  const mergedColumnsState: GridColumnsInitialState = {
    ...defaultState.columns,
    columnVisibilityModel: savedGridState?.columns?.columnVisibilityModel || defaultState.columns.columnVisibilityModel,
  };

  // Include orderedFields if it exists in saved state to preserve column order changes
  if (savedGridState?.columns?.orderedFields) {
    mergedColumnsState.orderedFields = savedGridState.columns.orderedFields;
  }

  const mergedState = {
    ...defaultState,
    pinnedColumns: savedGridState?.pinnedColumns,
    density: savedGridState?.density,
    sorting: savedGridState?.sorting,
    columns: mergedColumnsState,
    pagination: savedGridState?.pagination,
  };

  return mergedState;
}

export function useCXUserPrefs(params: UseCXUserPrefsParams) {
  const { gridId, gridColsDef, rowExpandLookup, treeData, saveUserConfig } = params;
  let { apiRef } = params;

  const hostPathname = useSettingsHostPathname();
  const localApiRef = useGridApiRef();
  if (!apiRef) apiRef = localApiRef;

  const initialSaveUserConfigRef = useRef(saveUserConfig);
  const configSettingsKey = initialSaveUserConfigRef.current
    ? getMuiGridSettingsKey(hostPathname, gridId)
    : "";
  const { userConfig, isLoading } = useGetUserConfig(configSettingsKey);
  const deleteUserConfig = useDeleteUserConfig();
  const { columnDefinitions, pinnedColumns, hiddenColumns, groupingColDef, sortedColumns } =
    gridColsDef;
  const [initialState, setInitialState] = useState<GridInitialState>();
  const [mergedInitialState, setMergedInitialState] = useState<CXGridPrefsState>();
  const [isDefaultView, setIsDefaultView] = useState(true);
  const [userConfigSetting, setUserConfigSetting] = useState<UserConfigDto>();
  const mergedConfigKeyRef = useRef<string>("");
  const lastConfigKeyRef = useRef<string>("");
  const hasRestoredInitialStateRef = useRef(false);
  const isRestoringRef = useRef(false);

  useEffect(() => {
    if (configSettingsKey && configSettingsKey !== lastConfigKeyRef.current) {
      setInitialState(undefined);
      setMergedInitialState(undefined);
      hasRestoredInitialStateRef.current = false;
      lastConfigKeyRef.current = configSettingsKey;
    }
  }, [configSettingsKey]);

  const hiddenColumnsObject = useMemo(
    () =>
      hiddenColumns?.reduce<Record<string, boolean>>(
        (object, item) => ({ ...object, [item]: false }),
        {}
      ),
    [hiddenColumns]
  );

  const defaultInitialState = useMemo(
    () => {
      // Extract default column field order from column definitions
      const defaultFieldOrder = columnDefinitions.map((colDef: GridColDef & { id?: string }) =>
        colDef.field || colDef.id || ""
      );

      const defaultState: CXGridPrefsState = {
        columns: {
          columnVisibilityModel: hiddenColumnsObject,
          orderedFields: defaultFieldOrder,
        },
        pagination: {
          paginationModel: DEFAULT_GRID_PAGINATION_MODEL,
        },
        pinnedColumns: pinnedColumns || { left: [], right: [] },
        sorting: {
          sortModel: sortedColumns,
        },
        density: "standard",
      };
      return defaultState;
    },
    [hiddenColumnsObject, pinnedColumns, sortedColumns, columnDefinitions]
  );

  const columns = useMemo<GridColDef[]>(() => {
    const cols = columnDefinitions.map((colDef) =>
      Object.fromEntries(
        Object.entries(colDef)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key === "id" ? "field" : key, value])
      )
    ) as GridColDef[];

    // build user-reordered columns methodically to avoid indexing issues
    const savedOrder = mergedInitialState?.columns?.orderedFields;
    if (savedOrder && Array.isArray(savedOrder)) {
      const colMap = new Map(cols.map(col => [col.field, col]));
      const reordered: GridColDef[] = [];
      const seen = new Set<string>();
      for (const field of savedOrder) {
        const col = colMap.get(field);
        if (col) {
          reordered.push(col);
          seen.add(field);
        }
      }
      // Columns added after the saved order was captured (e.g. tab-specific
      // columns) are placed next to their neighbour in the definition order
      // instead of being dumped at the far right.
      cols.forEach((col, index) => {
        if (seen.has(col.field)) return;
        let insertAt = reordered.length;
        for (let i = index + 1; i < cols.length; i++) {
          const anchor = reordered.findIndex((c) => c.field === cols[i].field);
          if (anchor !== -1) {
            insertAt = anchor;
            break;
          }
        }
        if (insertAt !== reordered.length) {
          reordered.splice(insertAt, 0, col);
          seen.add(col.field);
          return;
        }
        for (let i = index - 1; i >= 0; i--) {
          const anchor = reordered.findIndex((c) => c.field === cols[i].field);
          if (anchor !== -1) {
            insertAt = anchor + 1;
            break;
          }
        }
        reordered.splice(insertAt, 0, col);
        seen.add(col.field);
      });
      return reordered;
    }

    return cols;
  }, [columnDefinitions, mergedInitialState]);

  const isCurrentStateDefault = useCallback(() => {
    if (!apiRef?.current || !defaultInitialState) return false;
    const currentState = apiRef.current.exportState();
    return isDefaultGridState(defaultInitialState, currentState);
  }, [defaultInitialState, apiRef]);

  useEffect(() => {
    if (mergedConfigKeyRef.current === configSettingsKey && mergedInitialState) {
      return;
    }

    if (!initialState) {
      if (apiRef?.current) {
        setInitialState(apiRef.current.exportState());
      }
      return;
    }

    if (isLoading || !configSettingsKey) {
      return;
    }

    const stateToUse = userConfig?.settingsValue
      ? buildMergedState(defaultInitialState, userConfig.settingsValue)
      : { ...defaultInitialState };

    if (userConfig?.settingsValue) {
      setUserConfigSetting(userConfig);
    }

    mergedConfigKeyRef.current = configSettingsKey;
    setMergedInitialState(stateToUse);
    setIsDefaultView(!userConfig?.settingsValue);
  }, [isLoading, userConfig, initialState, configSettingsKey, defaultInitialState]);

  useEffect(() => {
    if (!apiRef?.current || !mergedInitialState) {
      return;
    }

    isRestoringRef.current = true;
    try {
      if (mergedInitialState.columns?.columnVisibilityModel) {
        apiRef.current.setColumnVisibilityModel(mergedInitialState.columns.columnVisibilityModel);
      }

      if (mergedInitialState.pinnedColumns) {
        apiRef.current.setPinnedColumns(mergedInitialState.pinnedColumns);
      }

      if (mergedInitialState.pagination?.paginationModel) {
        apiRef.current.setPaginationModel({
          ...mergedInitialState.pagination.paginationModel,
          page: 0,
        });
      }

      if (mergedInitialState.sorting?.sortModel) {
        apiRef.current.setSortModel(mergedInitialState.sorting.sortModel);
      }

      // Column order is now handled by reordering the columns above
      if (mergedInitialState.density) {
        apiRef.current.setDensity(mergedInitialState.density);
      }
    } finally {
      isRestoringRef.current = false;
    }

    hasRestoredInitialStateRef.current = true;
  }, [mergedInitialState, apiRef]);

  // The grid appends newly added fields to the end of its internal column
  // order, so re-apply our intended order whenever the column set changes.
  const columnFieldsKey = columns.map((col) => col.field).join("|");
  const lastColumnFieldsKeyRef = useRef("");
  useEffect(() => {
    if (!apiRef?.current || !mergedInitialState) return;
    if (lastColumnFieldsKeyRef.current === columnFieldsKey) return;

    const previousKey = lastColumnFieldsKeyRef.current;
    lastColumnFieldsKeyRef.current = columnFieldsKey;
    if (!previousKey) return;

    isRestoringRef.current = true;
    try {
      apiRef.current.restoreState({
        columns: { orderedFields: columnFieldsKey.split("|") },
      });
    } finally {
      isRestoringRef.current = false;
    }
  }, [columnFieldsKey, mergedInitialState, apiRef]);

  useRegisterDataGridSaveEvents(apiRef, gridId, saveUserConfig, defaultInitialState, isRestoringRef);

  useEffect(() => {
    if (!apiRef?.current) return;

    // Only subscribe to events after the initial state restore is complete
    if (!mergedInitialState) return;

    const resetDefaultView = () => {
      const isDefault = isCurrentStateDefault();
      setIsDefaultView(isDefault);
    };
    const unsubscribers = GRID_EVENTS.map((event) =>
      apiRef.current?.subscribeEvent(event, resetDefaultView)
    );

    return () => unsubscribers.forEach((unsub) => unsub?.());
  }, [apiRef, mergedInitialState, isCurrentStateDefault]);

  useEffect(() => {
    if (!apiRef?.current || !rowExpandLookup?.current) return;

    return apiRef.current.subscribeEvent("rowExpansionChange", (node: GridGroupNode) => {
      rowExpandLookup.current[node.id] = !!node.childrenExpanded;
    });
  }, [rowExpandLookup]);

  const handleResetTableView = useCallback(() => {
    if (userConfigSetting) {
      deleteUserConfig.mutate(userConfigSetting);
    }
    setUserConfigSetting(undefined);
    setMergedInitialState(undefined);
    mergedConfigKeyRef.current = "";
    if (apiRef?.current) {
      isRestoringRef.current = true;
      try {
        apiRef.current.setColumnVisibilityModel(defaultInitialState.columns?.columnVisibilityModel ?? {});
        apiRef.current.setPinnedColumns(defaultInitialState.pinnedColumns ?? { left: [], right: [] });
        apiRef.current.setPaginationModel(defaultInitialState.pagination?.paginationModel ?? DEFAULT_GRID_PAGINATION_MODEL);
        apiRef.current.setSortModel(defaultInitialState.sorting?.sortModel ?? []);
        apiRef.current.setDensity(defaultInitialState.density ?? "standard");
        if (defaultInitialState.columns?.orderedFields) {
          apiRef.current.restoreState({ columns: { orderedFields: defaultInitialState.columns.orderedFields } });
        }
      } finally {
        isRestoringRef.current = false;
      }
    }
    setIsDefaultView(true);
  }, [userConfigSetting, deleteUserConfig, apiRef, defaultInitialState]);

  const isGroupExpanded = useCallback(
    (node: GridGroupNode) => !!rowExpandLookup?.current?.[node.id],
    [rowExpandLookup]
  );

  const getTreeDataPath = useCallback((row: GridValidRowModel) => row.hierarchy, []);

  const dataGridTreeDataProps: Partial<DataGridProProps> = treeData
    ? {
        treeData: true,
        groupingColDef,
        ...(rowExpandLookup ? { isGroupExpandedByDefault: isGroupExpanded } : {}),
        getTreeDataPath,
      }
    : {};

  const finalInitialState = mergedInitialState || defaultInitialState;

  return {
    userConfigLoading: isLoading,
    handleResetTableView,
    isDefaultView,
    initialState,
    columns,
    defaultInitialState: finalInitialState,
    dataGridTreeDataProps,
  };
}
